import React, { useMemo } from "react";

import { Gauge, Timer } from "lucide-react";

import { WidgetCard } from "./StatWidgets";

const fmtDays = (v) => (v == null ? "—" : `${v % 1 === 0 ? v : v.toFixed(1)}`);

const BAR_GRADIENTS = [
    ["#93c5fd", "#3b82f6"], 
    ["#1e3a8a", "#1e293b"], 
    ["#f0abfc", "#a21caf"], 
    ["#6ee7b7", "#047857"], 
    ["#fdba74", "#c2410c"], 
];

const niceCeil = (value) => {
    if (value <= 0) return 1;
    const exp = Math.floor(Math.log10(value));
    const base = value / 10 ** exp;
    let niceBase;
    if (base <= 1) niceBase = 1;
    else if (base <= 2) niceBase = 2;
    else if (base <= 5) niceBase = 5;
    else niceBase = 10;
    return niceBase * 10 ** exp;
};

const VB_W = 520;
const VB_H = 260;
const PLOT_LEFT = 42;
const PLOT_RIGHT = VB_W - 16;
const PLOT_TOP = 18;
const PLOT_BOTTOM = VB_H - 38;
const PLOT_H = PLOT_BOTTOM - PLOT_TOP;
const TICK_COUNT = 4;

const VelocityPanel = ({ velocity, loading }) => {
    const durations = velocity?.statusDurations ?? [];
    const withValues = durations.filter((d) => d.avgDays != null);
    const maxDays = useMemo(
        () => durations.reduce((m, d) => Math.max(m, d.avgDays || 0), 0),
        [durations]
    );
    const slowestKey = velocity?.slowestStatusKey ?? null;

    const axisMax = niceCeil(maxDays * 1.1 || 1);
    const ticks = useMemo(
        () =>
            [...Array(TICK_COUNT + 1)].map((_, i) => (axisMax / TICK_COUNT) * i),
        [axisMax]
    );

    const plotW = PLOT_RIGHT - PLOT_LEFT;
    const slotW = durations.length ? plotW / durations.length : 0;
    const barW = Math.min(46, slotW * 0.5);

    return (
        <WidgetCard icon={Gauge} title="Speed & Velocity" insight={velocity?.insight} loading={loading}>
            <div className="grid grid-cols-3 gap-2">
                <MiniStat
                    label="Avg time to hire"
                    value={fmtDays(velocity?.avgTimeToHireDays)}
                    unit="d"
                    loading={loading}
                    accent="#6366f1"
                />
                <MiniStat
                    label="Median / p90"
                    value={
                        velocity?.medianTimeToHireDays == null
                            ? "—"
                            : `${fmtDays(velocity.medianTimeToHireDays)} / ${fmtDays(velocity.p90TimeToHireDays)}`
                    }
                    unit={velocity?.medianTimeToHireDays == null ? "" : "d"}
                    loading={loading}
                    accent="#0ea5e9"
                />
                <MiniStat
                    label="Applied → interviewed"
                    value={velocity?.appliedToInterviewRate == null ? "—" : Math.round(velocity.appliedToInterviewRate)}
                    unit={velocity?.appliedToInterviewRate == null ? "" : "%"}
                    loading={loading}
                    accent="#22c55e"
                />
            </div>

            <div className="flex items-center gap-1.5 mt-1">
                <Timer size={12} className="text-text-primary/60" />
                <span className="text-[11px] font-medium text-text-secondary">
                    Avg days spent in each stage
                </span>
            </div>
            {loading ? (
                <div className="h-[240px] w-full rounded-md bg-background-tertiary animate-pulse" />
            ) : withValues.length === 0 ? (
                <p className="text-[11px] text-text-secondary py-3 text-center">
                    Not enough stage transitions recorded yet.
                </p>
            ) : (
                <svg viewBox={`0 0 ${VB_W} ${VB_H}`} width="100%" height={240} className="overflow-visible">
                    <defs>
                        {durations.map((d, i) => {
                            const [c0, c1] = BAR_GRADIENTS[i % BAR_GRADIENTS.length];
                            return (
                                <linearGradient key={d.key} id={`velGrad-${d.key}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor={c0} />
                                    <stop offset="100%" stopColor={c1} />
                                </linearGradient>
                            );
                        })}
                    </defs>

                    {ticks.map((t, i) => {
                        const y = PLOT_BOTTOM - (t / axisMax) * PLOT_H;
                        return (
                            <g key={i}>
                                <line
                                    x1={PLOT_LEFT}
                                    x2={PLOT_RIGHT}
                                    y1={y}
                                    y2={y}
                                    className="stroke-border-primary"
                                    strokeWidth={1}
                                    strokeDasharray={i === 0 ? "0" : "3,3"}
                                />
                                <text
                                    x={PLOT_LEFT - 8}
                                    y={y}
                                    textAnchor="end"
                                    dominantBaseline="middle"
                                    fontSize="10"
                                    className="fill-text-secondary"
                                >
                                    {Math.round(t)}
                                </text>
                            </g>
                        );
                    })}

                    {/* Y-axis line + arrowhead */}
                    <line x1={PLOT_LEFT} x2={PLOT_LEFT} y1={PLOT_TOP - 6} y2={PLOT_BOTTOM} className="stroke-text-primary" strokeWidth={1.5} />
                    <polygon
                        points={`${PLOT_LEFT - 4},${PLOT_TOP - 6} ${PLOT_LEFT + 4},${PLOT_TOP - 6} ${PLOT_LEFT},${PLOT_TOP - 14}`}
                        className="fill-text-primary"
                    />

                    {/* X-axis line + arrowhead */}
                    <line x1={PLOT_LEFT} x2={PLOT_RIGHT + 8} y1={PLOT_BOTTOM} y2={PLOT_BOTTOM} className="stroke-text-primary" strokeWidth={1.5} />
                    <polygon
                        points={`${PLOT_RIGHT + 8},${PLOT_BOTTOM - 4} ${PLOT_RIGHT + 8},${PLOT_BOTTOM + 4} ${PLOT_RIGHT + 16},${PLOT_BOTTOM}`}
                        className="fill-text-primary"
                    />

                    {/* Bars */}
                    {durations.map((d, i) => {
                        const cx = PLOT_LEFT + slotW * i + slotW / 2;
                        const isSlow = slowestKey && d.key === slowestKey;
                        const h = d.avgDays ? (d.avgDays / axisMax) * PLOT_H : 0;
                        const y = PLOT_BOTTOM - h;
                        return (
                            <g key={d.key}>
                                {d.avgDays != null ? (
                                    <>
                                        <rect
                                            x={cx - barW / 2}
                                            y={y}
                                            width={barW}
                                            height={h}
                                            rx={3}
                                            fill={`url(#velGrad-${d.key})`}
                                            stroke={isSlow ? "#f59e0b" : "none"}
                                            strokeWidth={isSlow ? 2.5 : 0}
                                        />
                                        <text
                                            x={cx}
                                            y={y - 8}
                                            textAnchor="middle"
                                            fontSize="11"
                                            fontWeight={isSlow ? 700 : 600}
                                            fill={isSlow ? "#f59e0b" : undefined}
                                            className={isSlow ? undefined : "fill-text-primary"}
                                        >
                                            {fmtDays(d.avgDays)}d
                                        </text>
                                    </>
                                ) : (
                                    <text
                                        x={cx}
                                        y={PLOT_BOTTOM - 6}
                                        textAnchor="middle"
                                        fontSize="10"
                                        className="fill-text-secondary"
                                    >
                                        —
                                    </text>
                                )}
                                {/* Category label */}
                                <text
                                    x={cx}
                                    y={PLOT_BOTTOM + 18}
                                    textAnchor="middle"
                                    fontSize="11"
                                    fontWeight={500}
                                    className="fill-text-primary"
                                >
                                    {d.label}
                                </text>
                            </g>
                        );
                    })}
                </svg>
            )}
        </WidgetCard>
    );
};

const MiniStat = ({ label, value, unit, accent, loading }) => (
    <div
        className="rounded-lg bg-background-tertiary/60 border border-border-primary px-2.5 py-2 flex flex-col gap-1"
        style={{ borderTopWidth: 2, borderTopColor: accent }}
    >
        {loading ? (
            <div className="h-6 w-12 rounded bg-background-tertiary animate-pulse" />
        ) : (
            <p className="text-[1.15rem] font-bold leading-none text-text-primary tabular-nums">
                {value}
                {unit ? <span className="text-[0.8rem] font-semibold text-text-secondary ml-0.5">{unit}</span> : null}
            </p>
        )}
        <p className="text-[10px] text-text-secondary leading-tight">{label}</p>
    </div>
);

export default VelocityPanel;
